import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { CurrencyDollarIcon, ArrowTrendingUpIcon, CalendarIcon } from '@heroicons/react/24/outline';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const RevenueReport = ({ data, dateRange }) => {
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Make sure data exists and has the expected structure
  // Handle both API response format and placeholder data format
  const reportData = data?.data || data || {};
  
  // Convert revenue_by_date object to array for chart
  const dailyRevenueArray = reportData.revenue_by_date ? 
    Object.entries(reportData.revenue_by_date).map(([date, data]) => ({
      date,
      amount: data.amount
    })) : [];
  
  // Daily revenue chart data
  const dailyRevenueData = {
    labels: Array.isArray(dailyRevenueArray) ? dailyRevenueArray.map(item => formatDate(item.date)) : [],
    datasets: [
      {
        label: 'Daily Revenue',
        data: Array.isArray(dailyRevenueArray) ? dailyRevenueArray.map(item => item.amount) : [],
        borderColor: 'rgb(79, 70, 229)',
        backgroundColor: 'rgba(79, 70, 229, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Revenue by tour chart data
  const tourData = Array.isArray(reportData.revenue_by_tour) ? reportData.revenue_by_tour : [];
  
  const revenueByTourData = {
    labels: tourData.map(item => item.name || 'Unknown Tour'),
    datasets: [
      {
        label: 'Revenue by Tour',
        data: tourData.map(item => item.amount || 0),
        backgroundColor: [
          'rgba(79, 70, 229, 0.7)',
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(239, 68, 68, 0.7)',
          'rgba(139, 92, 246, 0.7)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Revenue by payment method chart data
  // Convert the object to array for the chart
  const paymentMethodArray = reportData.revenue_by_payment_method ? 
    Object.entries(reportData.revenue_by_payment_method).map(([method, data]) => ({
      method,
      amount: data.amount
    })) : [];
    
  const revenueByPaymentMethodData = {
    labels: Array.isArray(paymentMethodArray) ? paymentMethodArray.map(item => item.method || 'Unknown') : [],
    datasets: [
      {
        label: 'Revenue by Payment Method',
        data: Array.isArray(paymentMethodArray) ? paymentMethodArray.map(item => item.amount || 0) : [],
        backgroundColor: [
          'rgba(79, 70, 229, 0.7)',
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Daily Revenue Trend',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Revenue by Tour',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  const doughnutChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Revenue by Payment Method',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${formatCurrency(value)} (${percentage}%)`;
          }
        }
      }
    },
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Revenue Report</h2>
        <p className="text-sm text-gray-500 mb-6">
          Revenue data from {formatDate(reportData.period?.start_date || dateRange.startDate)} to {formatDate(reportData.period?.end_date || dateRange.endDate)}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{formatCurrency(reportData.total_revenue || 0)}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowTrendingUpIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Average Booking Value</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{formatCurrency(reportData.average_transaction || 0)}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Transactions</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">
                      {reportData.total_transactions || 0}
                    </div>
                    <div className="text-sm text-gray-500">
                      transactions
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="space-y-8">
        {/* Daily Revenue Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Revenue Trend</h3>
          <div className="h-80">
            <Line data={dailyRevenueData} options={lineChartOptions} />
          </div>
        </div>

        {/* Revenue by Tour and Payment Method Charts */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue by Tour</h3>
            <div className="h-80">
              <Bar data={revenueByTourData} options={barChartOptions} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue by Payment Method</h3>
            <div className="h-80 flex items-center justify-center">
              <Doughnut data={revenueByPaymentMethodData} options={doughnutChartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue by Tour Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue by Tour</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tour Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(tourData) ? tourData.map((tour, index) => {
                  const percentage = reportData.total_revenue ? (tour.amount / reportData.total_revenue) * 100 : 0;
                  return (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {tour.name || 'Unknown Tour'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {formatCurrency(tour.amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {percentage.toFixed(1)}%
                      </td>
                    </tr>
                  );
                }) : <tr><td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">No tour data available</td></tr>}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    Total
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    {formatCurrency(reportData.total_revenue || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    100%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueReport;
